// Holiday & Flight API Types

import { ComboHolidayType } from '../BookingMask';

export interface HolidaySearchParams {
  search: {
    departure: string;
    arrival: string;
    departureDate: string;
    arrivalDate?: string;
    adults: number;
    children: number;
    infants: number;
    rooms: number;
    currency: string;
    isRoundTrip: boolean;
    isIncludedHotel: boolean;
    isIncludedTicket: boolean;
  };
  filters?: {
    priceRange?: {
      min: number;
      max: number;
    };
  };
}

export interface FlightSearchParams {
  sessionId: string;
  filters?: {
    departureTime?: {
      from: string;
      to: string;
    };
    arrivalTime?: {
      from: string;
      to: string;
    };
    cabinIds?: string[];
  };
}

// Flight Data Structures
export interface AirportInfo {
  code: string;
  name: string;
  datetime: string;
  terminal?: string;
}

export interface AircraftInfo {
  type: string;
  name: string;
}

export interface Itinerary {
  tripId: number;
  segmentId: number;
  flightDate: string;
  carrier: string;
  carrierName: string;
  flightNumber: number;
  duration: string;
  departureInfo: AirportInfo;
  arrivalInfo: AirportInfo;
  aircraftInfo: AircraftInfo;
}

export interface CabinInfo {
  name: string;
  id: number;
}

export interface SegmentFare {
  fareBasis: string;
  bookingClass: string;
  bookingClassName: string;
  segmentId: number;
  seatAvailability: number;
  cabin: CabinInfo;
}

export interface BaggageAllowance {
  weight: number;
}

export interface CombineRule {
  route: string;
  rules: {
    tripId: string;
    bookingClass: string[];
  }[];
}

export interface Pricing {
  route: string;
  bookingClass: string;
  bookingClassName: string;
  segmentFare: SegmentFare[];
  currency: string;
  additionalAmount: number;
  paxAdditionalAmount: number;
  baggageAllowance: BaggageAllowance;
  combineRules?: CombineRule[];
}

export interface Trip {
  tripId: string;
  listItinerary: Itinerary[];
  listPricing: Pricing[];
}

export interface FlightRoute {
  departure: string;
  arrival: string;
  person: number;
  route: string;
  listTrip: Trip[];
}

export interface FlightOption {
  directOnly: boolean;
  flexible: boolean;
  tripType: string;
  currency: string;
}

export interface FlightData {
  listFlight: FlightRoute[];
  dataSessionId: string;
  option: FlightOption;
}

// Ticket Data Structures
export interface TicketItem {
  id: string;
  code: string;
  imageUrl: string | null;
  otaSiteCode: string;
  otaSiteName: string;
  otaProductCode: string;
  languageCode: string | null;
  totalPrice: number;
  paxPrice: number;
  additionalPrice: number;
  paxAdditionalPrice: number;
  commission?: number | string;
  discount?: number;
  totalPayment?: number;
  multiple: number;
  minQuantity: number;
  comboIds: any[];
  comboCode?: string;
}

export interface TicketGroup {
  name: string;
  description: string;
  [key: string]: TicketItem | string; // numbered keys (0,1,2...) are TicketItem
}

export interface Tickets {
  [siteCode: string]: TicketGroup;
}

export interface ComboSearchResponse {
  combo: {
    flight: FlightData;
    tickets: Tickets;
  };
}

// Session Storage Types
export interface SelectedFlight {
  route: FlightRoute;
  trip: Trip;
  pricing: Pricing;
  sessionId: string;
  selectedAt: string;
}

export interface SelectedTicket {
  ticket: TicketItem;
  siteCode: string;
  siteName: string;
  productCode: string;
  quantity: number;
  selectedAt: string;
  isIncludeAllTicket?: boolean;
  comboType?: ComboHolidayType;
}
export interface SelectedHotel {
  hotel: Hotel;
  roomTypes: RoomTypeItem[];
  media: Media;
  totalPrice: number;
  vndTotalPrice: number;
  paxPrice: number;
  vndPaxPrice: number;
  person: number;
  savingPercent: number;
}

export interface Hotel {
  id: string;
  name: string;
  starRating: number;
  currency: string;
  logoUrl: string;
  address: string;
  amenities: HotelAmenity[];
  tags: string[];
  description: string;
  shortDescription: string;
  images: HotelImage[];
  checkInTime: string;
  checkOutTime: string;
  childPolicy: string;
  extraBedPolicy: string;
  breakfastPolicy: string;
  petPolicy: string;
  termsAndConditions: string;
}
export interface HotelAmenity {
  code: string;
  name: string;
}
export interface HotelImage {
  url: string;
  title: string;
  description: string;
  isMain: boolean;
  displayOrder: number;
}
export interface RoomTypeItem {
  roomType: RoomType;
  pricing: Pricing;
  availability: Availability;
  roomImages: HotelImage[];
}
export interface RoomType {
  id: string;
  code: string;
  name: string;
  description: string;
  maxOccupancy: number;
  adultCapacity: number;
  childCapacity: number;
  roomSize: number;
  viewType: string;
  amenities: RoomAmenity[];
  images: HotelImage[];
  tags: string[];
}
export interface RoomAmenity {
  id: string;
  name: string;
}
export interface Pricing {
  totalPrice: number;
  displayTotalPrice: number;
  paxPrice: number;
  displayPaxPrice: number;
  additionalAmount: number;
  displayAdditionalAmount: number;
  paxAdditionalAmount: number;
  displayPaxAdditionalAmount: number;
  avgNightlyRate: number;
  displayAvgNightlyRate: number;
  currency: string;
  dailyBreakdown: any[];
  ratePlanId: string;
  ratePlanCode: string;
  ratePlanName: string;
  rateDescription: string;
  rateTags: string[];
  tickets: Ticket[];
  comboId: string;
  isIncludeAllTicket: boolean;
}
export interface Ticket {
  id: string;
  code: string;
  imageUrl: string;
  otaSiteCode: string;
  otaSiteName: string;
  otaProductCode: string;
  additionalAmount: number;
  displayAdditionalAmount: number;
  paxAdditionalAmount: number;
  displayPaxAdditionalAmount: number;
  name: string;
  description: string;
  media: string[];
}
export interface Availability {
  roomsAvailable: number;
  isAvailable: boolean;
}
export interface Media {
  hotelImages: HotelImage[];
  roomImages: HotelImage[];
}

// Session Storage Keys
export const SESSION_KEYS = {
  SELECTED_FLIGHTS: 'holiday_selected_flights',
  SELECTED_TICKETS: 'holiday_selected_tickets',
  SELECTED_HOTELS: 'holiday_selected_hotels',
  COMBO_SESSION_ID: 'holiday_combo_session_id',
  SEARCH_PARAMS: 'holiday_search_params',
  PASSENGER_INFO_FORM: 'holiday_passenger_info_form',
  SPECIAL_REQUEST: 'holiday_special_request',
  SESSION_USER: 'holiday_session_user',
} as const;
